import { useCurrentUserLevel } from "@/store/selectors/userProfileSelectors";
import { Box, Group, Tooltip, ActionIcon } from "@mantine/core";
import { Text } from "../ui/Text";
import { Badge } from "../ui/Badge";
import { IconStar, IconStarFilled, IconInfoCircle } from "@tabler/icons-react";
import { useStyles } from "@/styles/useStyles";
import { Link } from "../ui/Link";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useStore } from "@/store";
import { IUserLevelConfig } from "@/configs/levels";
import { Analytics } from "@/services/Analytics";


const levelIconSize = 22;

export const UserProfileLevel = () => {
  const currentUserLevel = useCurrentUserLevel();
  const { theme } = useStyles();
  const didLoadUserData = useStore((state) => state.user.didLoadUserData);
  const [canTrackLevelUp, setCanTrackLevelUp] = useState(false);

  // Keep visible level in state to allow animation when level changes
  const [visibleLevel, setVisibleLevel] = useState<IUserLevelConfig | null>(
    currentUserLevel,
  );
  const [floatingStars, setFloatingStars] = useState<number[]>([]);
  const [isBadgeAnimating, setIsBadgeAnimating] = useState(false);
  const [isLevelCompleting, setIsLevelCompleting] = useState(false);
  const previousLevelRef = useRef<IUserLevelConfig | null>(currentUserLevel);
  const setBadgeConfetti = useStore(
    (state) => state.experience.setBadgeConfetti,
  );

  // Wait for data to load, then add a delay before allowing level up tracking
  // This prevents false level ups during initial data load
  useEffect(() => {
    if (didLoadUserData && !canTrackLevelUp) {
      // Wait 3 seconds after data loads before allowing level up tracking
      const timer = setTimeout(() => {
        setCanTrackLevelUp(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [didLoadUserData, canTrackLevelUp]);

  useEffect(() => {
    const previousLevel = previousLevelRef.current;

    // Detect Level 1 - shake to draw attention for new users
    if (
      currentUserLevel !== null &&
      currentUserLevel.level === 1
      // &&
      // (previousLevel === null || previousLevel.level !== 1)
    ) {
      // Trigger badge shake animation to draw attention
      setIsBadgeAnimating(true);
      setTimeout(() => {
        setIsBadgeAnimating(false);
      }, 600);

      // Update visible level immediately
      setVisibleLevel(currentUserLevel);
    }
    // Detect level increase: previous level was not null, and either:
    // 1. Current level is null (all levels complete), or
    // 2. Current level number is higher than previous level number
    else if (
      previousLevel !== null &&
      currentUserLevel !== previousLevel &&
      currentUserLevel !== null &&
      currentUserLevel.level !== 1 &&
      (currentUserLevel === null ||
        (currentUserLevel !== null &&
          currentUserLevel.level > previousLevel.level))
    ) {
      // Step 1: Show completion state - fill star and pulse
      setIsLevelCompleting(true);

      // Step 2: After pulse completes, trigger shake and float animations
      setTimeout(() => {
        setIsLevelCompleting(false);

        // Trigger badge shake animation
        setIsBadgeAnimating(true);
        setTimeout(() => {
          setIsBadgeAnimating(false);
        }, 600);

        // Trigger floating star animation
        const id = Date.now();
        setFloatingStars((prev) => [...prev, id]);
        setTimeout(() => {
          setFloatingStars((prev) => prev.filter((i) => i !== id));
        }, 700);

        // Trigger confetti from bottom left corner
        setBadgeConfetti({ x: 0, y: 100 });

        // Track level up analytics (only if data has loaded and delay has passed)
        if (canTrackLevelUp && previousLevel?.level) {
          Analytics.levelUp({
            value: currentUserLevel.level,
            label: `Level ${previousLevel.level} to ${currentUserLevel.level}`,
            action: `Level Up: ${previousLevel.level} → ${currentUserLevel.level}`,
            context: currentUserLevel.title,
          });
        }

        // Step 3: Update visible level after shake starts
        setTimeout(() => {
          setVisibleLevel(currentUserLevel);
        }, 100);
      }, 1500); // Pulse duration
    } else {
      // Update visible level immediately if no level increase detected
      setVisibleLevel(currentUserLevel);
      setIsLevelCompleting(false);
    }

    // Update ref for next comparison
    previousLevelRef.current = currentUserLevel;
  }, [currentUserLevel, setBadgeConfetti, canTrackLevelUp]);

  const levelLinkLabel = useMemo(() => {
    if (!visibleLevel) return "";
    const title = visibleLevel.title || "";
    const status = visibleLevel.statusString
      ? ` (${visibleLevel.statusString})`
      : "";
    return `${title}${status}`;
  }, [visibleLevel]);

  const handleLevelLinkClick = useCallback(
    (event: any) => {
      Analytics.click({
        action: "User profile level link",
        label: levelLinkLabel,
        event,
      });
    },
    [levelLinkLabel],
  );

  return (
    <>
      <style>
        {`
          @keyframes floatUpReaction {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            50% {
              opacity: 0.9;
              transform: translateY(-30px) scale(1.6);
            }
            100% {
              opacity: 0;
              transform: translateY(-60px) scale(1);
            }
          }
          @keyframes badgePulse {
            0%, 100% {
              transform: scale(1);
            }
            25% {
              transform: scale(1.1);
            }
            50% {
              transform: scale(1.15);
            }
            75% {
              transform: scale(1.1);
            }
          }
          @keyframes badgeShake {
            0%, 100% {
              transform: scale(1) rotate(0deg);
            }
            10% {
              transform: scale(1.15) rotate(-3deg);
            }
            20% {
              transform: scale(1.15) rotate(3deg);
            }
            30% {
              transform: scale(1.15) rotate(-3deg);
            }
            40% {
              transform: scale(1.15) rotate(3deg);
            }
            50% {
              transform: scale(1.2) rotate(-2deg);
            }
            60% {
              transform: scale(1.2) rotate(2deg);
            }
            70% {
              transform: scale(1.15) rotate(-1deg);
            }
            80% {
              transform: scale(1.1) rotate(1deg);
            }
            90% {
              transform: scale(1.05) rotate(0deg);
            }
          }
        `}
      </style>
      <Group
        gap="sm"
        align="center"
        justify="center"
        className="w-full"
        wrap="nowrap"
      >
        <Box pos="relative" display="flex">
          <Link to="/profile">
            <Badge
              ariaLabel={
                !visibleLevel
                  ? "All levels complete!"
                  : `Level ${visibleLevel.level}`
              }
              color="primary"
              variant="light"
              size="xl"
              fz="16px"
              px="lg"
              radius="xl"
              style={{
                animation: isLevelCompleting
                  ? "badgePulse 1500ms ease-in-out"
                  : isBadgeAnimating
                    ? "badgeShake 600ms ease-in-out"
                    : undefined,
                transition: "transform 0.2s ease-out",
              }}
              leftSection={
                !visibleLevel || isLevelCompleting ? (
                  <IconStarFilled size={levelIconSize} color={theme.colors.primary[7]} />
                ) : (
                  <IconStar size={levelIconSize} color={theme.colors.primary[7]} />
                )
              }
            >
              {!visibleLevel?.level
                ? "All levels complete!"
                : `Level ${visibleLevel.level}`}
            </Badge>
          </Link>
          {floatingStars.map((id) => (
            <Box
              key={id}
              pos="absolute"
              top={4}
              left="50%"
              style={{
                animation: "floatUpReaction 700ms ease-out forwards",
                pointerEvents: "none",
                display: "flex",
                zIndex: 1000,
                transform: "translateX(-50%)",
              }}
            >
              <IconStarFilled size={levelIconSize} color={theme.colors.primary[7]} />
            </Box>
          ))}
        </Box>
        {visibleLevel ? (
          <Group gap="xs" align="center" wrap="nowrap">
            <Link
              to={visibleLevel?.href || "/profile"}
              onClick={handleLevelLinkClick}
            >
              <Text
                titleStyle="h5"
                fw={700}
                size="sm"
                c="primary.8"
                style={{ whiteSpace: "nowrap" }}
              >
                {visibleLevel?.title || ""}
                {visibleLevel.statusString && (
                  <>
                    {" "}
                    <span
                      style={{
                        fontSize: "0.9rem",
                        color: theme.colors.gray[7],
                        fontWeight: 400,
                      }}
                    >
                      ({visibleLevel.statusString})
                    </span>
                  </>
                )}
              </Text>
            </Link>
            {visibleLevel.tooltip && (
              <Tooltip
                label={visibleLevel.tooltip}
                events={{ hover: true, focus: true, touch: true }}
                position="top"
                withArrow
              >
                <ActionIcon
                  variant="transparent"
                  size="sm"
                  color="gray"
                  aria-label="More information"
                >
                  <IconInfoCircle size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        ) : null}
      </Group>
    </>
  );
};
